import { partition } from 'lodash';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import { NoteView } from '@ndvr/customer-api';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/utils/cn';
import { PersonDrawer } from 'modules';
import { personRoute } from 'utils';
import { EventDetailsDrawerByGuid } from 'views/household/HouseholdEvents/EventDetailsDrawer';
import { CreateOpportunitySheet } from 'views/opportunities/widgets/CreateOpportunitySheet';
import { toSuggestedOpportunityFormValues } from 'views/opportunities/widgets/OpportunityForm';
import { OpportunityViewDetailsSheet } from 'views/opportunities/widgets/OpportunityViewDetailsSheet';
import { toSuggestedPersonFormValues } from 'views/person/person-form-utils';
import { CreateTaskSheet } from 'views/tasks/widgets/CreateTaskSheet';
import { toActionItemFormValues } from 'views/tasks/widgets/TaskForm';
import { TaskViewDetailsSheet } from 'views/tasks/widgets/TaskViewDetailsSheet';

import { MEETING_MINUTES_LINK_CLASSES } from '../meeting-minutes-list-utils';
import { useDismissNoteSuggestion, useNoteSuggestions } from '../note-suggestion-queries';
import { NoteSuggestion, NoteSuggestionType } from '../note-suggestion-utils';
import { NewEventSheet, toSuggestedMeetingFormValues } from './NewEventSheet';

type SuggestionKind<T extends NoteSuggestionType> = {
    label: string;
    classes: string;
    // A row's action: create the entity while pending, open it once created.
    renderPending: (suggestion: Extract<NoteSuggestion, { type: T }>, noteGUID: string) => ReactNode;
    renderCreated: (createdGUID: string) => ReactNode;
};

const SUGGESTION_CTA_CLASSES =
    'border-cocoa-900 bg-cocoa-900 text-cocoa-0 shrink-0 rounded-[3px] border px-[9px] py-1 text-[10px] font-medium tracking-[0.06em] uppercase';

// Sheets are mounted only while open, so a controlled sheet's onOpenChange(false) maps to onClose.
const whenClosed = (onClose: () => void) => (open: boolean) => {
    if (!open) onClose();
};

const SUGGESTION_KINDS: { [T in NoteSuggestionType]: SuggestionKind<T> } = {
    TASK: {
        label: 'Task',
        classes: 'bg-cocoa-900 text-cocoa-0',
        renderPending: ({ actionItem }, noteGUID) => (
            <SheetButton
                label="Create task"
                renderSheet={(onClose) => (
                    <CreateTaskSheet
                        householdGUID={actionItem.householdGUID}
                        defaultFormValues={toActionItemFormValues(actionItem)}
                        actionItemGUID={actionItem.actionItemGUID}
                        noteGUID={noteGUID}
                        onClose={onClose}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open task"
                renderSheet={(onClose) => (
                    <TaskViewDetailsSheet open taskGuid={createdGUID} onOpenChange={whenClosed(onClose)} />
                )}
            />
        ),
    },
    OPPORTUNITY: {
        label: 'Opportunity',
        classes: 'bg-seafoam-300 text-cocoa-900',
        renderPending: ({ suggestedOpportunity }, noteGUID) => (
            <SheetButton
                label="Create opportunity"
                renderSheet={(onClose) => (
                    <CreateOpportunitySheet
                        householdGUID={suggestedOpportunity.householdGUID}
                        defaultFormValues={toSuggestedOpportunityFormValues(suggestedOpportunity)}
                        link={{ suggestedOpportunityGUID: suggestedOpportunity.suggestedOpportunityGUID, noteGUID }}
                        onClose={onClose}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open opportunity"
                renderSheet={(onClose) => (
                    <OpportunityViewDetailsSheet
                        open
                        opportunityGuid={createdGUID}
                        onOpenChange={whenClosed(onClose)}
                    />
                )}
            />
        ),
    },
    PERSON: {
        label: 'Person',
        classes: 'bg-seafoam-100 text-cocoa-900',
        renderPending: ({ suggestedPerson }, noteGUID) => (
            <SheetButton
                label="Add person"
                renderSheet={(onClose) => (
                    <PersonDrawer
                        mode="create-household"
                        globalAccountGUID={suggestedPerson.householdGUID}
                        defaultValues={toSuggestedPersonFormValues(suggestedPerson)}
                        link={{ suggestedPersonGUID: suggestedPerson.suggestedPersonGUID, noteGUID }}
                        onClose={onClose}
                    />
                )}
            />
        ),
        // People have no view sheet, so this is a real link to the person page.
        renderCreated: (createdGUID) => (
            <Link to={personRoute(createdGUID)} className={SUGGESTION_CTA_CLASSES}>
                Open person
            </Link>
        ),
    },
    MEETING: {
        label: 'Meeting',
        classes: 'bg-seafoam-500 text-cocoa-0',
        renderPending: ({ suggestedMeeting }, noteGUID) => (
            <SheetButton
                label="Schedule meeting"
                renderSheet={(onClose) => (
                    <NewEventSheet
                        open
                        onOpenChange={whenClosed(onClose)}
                        defaultHousehold={{ value: suggestedMeeting.householdGUID, label: '' }}
                        disableHouseholdSelection
                        defaultValues={toSuggestedMeetingFormValues(suggestedMeeting)}
                        link={{ suggestedMeetingGUID: suggestedMeeting.suggestedMeetingGUID, noteGUID }}
                    />
                )}
            />
        ),
        renderCreated: (createdGUID) => (
            <SheetButton
                label="Open meeting"
                renderSheet={(onClose) => (
                    <EventDetailsDrawerByGuid eventGUID={createdGUID} isOpen onOpenChange={whenClosed(onClose)} />
                )}
            />
        ),
    },
};

const SUGGESTION_ROW_CLASSES = 'border-cocoa-100 flex flex-col gap-[5px] border-b pb-2';

type CreatedNoteSuggestion = NoteSuggestion & { createdGUID: string };

const isCreated = (suggestion: NoteSuggestion): suggestion is CreatedNoteSuggestion => !!suggestion.createdGUID;

export const MeetingMinutesSuggestions = ({ note }: { note: NoteView }) => {
    const { data: suggestions, isLoading, isError } = useNoteSuggestions(note.noteGUID);
    const [created, pending] = partition(suggestions, isCreated);
    const pendingLabel = pending.length === 1 ? '1 suggestion' : `${pending.length} suggestions`;

    return (
        <div className="bg-cocoa-0 flex min-w-0 flex-col gap-2 rounded-md p-3.5">
            {/* Waits for every source like the skeleton does, so the card never grows a section mid-load. */}
            {!isLoading && created.length > 0 && (
                <>
                    <SectionHeader count={`${created.length} created`}>
                        From Claire&trade;&apos;s suggestions
                    </SectionHeader>
                    <div className="flex flex-col gap-2">
                        {created.map((suggestion) => (
                            <CreatedSuggestionRow key={suggestion.suggestionGUID} suggestion={suggestion} />
                        ))}
                    </div>
                    <Separator className="bg-cocoa-300" />
                </>
            )}
            <SectionHeader count={isLoading || isError ? undefined : pendingLabel}>Claire&trade; found</SectionHeader>
            {isLoading ? (
                <SuggestionsSkeleton />
            ) : isError ? (
                <p className="text-cocoa-500 py-1.5 text-[11px]">Unable to load suggestions</p>
            ) : pending.length === 0 ? (
                <p className="text-cocoa-500 py-1.5 text-[11px]">Nothing left to review.</p>
            ) : (
                pending.map((suggestion) => (
                    <PendingSuggestionRow
                        key={suggestion.suggestionGUID}
                        suggestion={suggestion}
                        noteGUID={note.noteGUID}
                    />
                ))
            )}
        </div>
    );
};

const SectionHeader = ({ children, count }: { children: ReactNode; count?: string }) => (
    <div className="flex items-baseline justify-between">
        <span className="text-cocoa-500 text-[10px] font-medium tracking-[0.16em] uppercase">{children}</span>
        {count && <span className="text-cocoa-500 text-[10px]">{count}</span>}
    </div>
);

const PendingSuggestionRow = ({ suggestion, noteGUID }: { suggestion: NoteSuggestion; noteGUID: string }) => {
    const kind = SUGGESTION_KINDS[suggestion.type];
    // TS cannot correlate suggestion.type with the matching registry entry, so widen the renderer
    // to the full union here; each entry was type-checked against its own kind above.
    const renderPending = kind.renderPending as SuggestionKind<NoteSuggestionType>['renderPending'];
    const dismiss = useDismissNoteSuggestion(noteGUID);
    // Optimistic: the row disappears on click and only comes back if the dismiss request fails.
    if (dismiss.isPending) return null;
    return (
        <SuggestionRowLayout suggestion={suggestion}>
            <button
                type="button"
                className={cn(MEETING_MINUTES_LINK_CLASSES, 'shrink-0')}
                onClick={() => dismiss.mutate(suggestion)}
            >
                Dismiss
            </button>
            {renderPending(suggestion, noteGUID)}
        </SuggestionRowLayout>
    );
};

// Created rows always sit in their own section container, so the last one can drop its border
// above the divider.
const CreatedSuggestionRow = ({ suggestion }: { suggestion: CreatedNoteSuggestion }) => (
    <SuggestionRowLayout suggestion={suggestion} className="last:border-b-0 last:pb-0">
        {SUGGESTION_KINDS[suggestion.type].renderCreated(suggestion.createdGUID)}
    </SuggestionRowLayout>
);

// Badge and title, then meta with the row's action on the right.
const SuggestionRowLayout = ({
    suggestion,
    className,
    children,
}: {
    suggestion: NoteSuggestion;
    className?: string;
    children: ReactNode;
}) => {
    const kind = SUGGESTION_KINDS[suggestion.type];
    return (
        <div className={cn(SUGGESTION_ROW_CLASSES, className)}>
            <div className="flex items-baseline gap-2">
                <span
                    className={cn(
                        `shrink-0 rounded-[3px] px-1.5 py-[3px] text-[8px] font-medium tracking-[0.1em] uppercase`,
                        kind.classes,
                    )}
                >
                    {kind.label}
                </span>
                <span className="text-xs leading-[1.45]">{suggestion.title}</span>
            </div>
            <div className="flex items-center gap-2.5">
                <span className="text-cocoa-500 min-w-0 flex-1 truncate text-[11px]">{suggestion.meta}</span>
                {children}
            </div>
        </div>
    );
};

// The row CTA; its sheet is mounted only while open and closing it hands control back here.
const SheetButton = ({ label, renderSheet }: { label: string; renderSheet: (onClose: () => void) => ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <button type="button" onClick={() => setIsOpen(true)} className={SUGGESTION_CTA_CLASSES}>
                {label}
            </button>
            {isOpen && renderSheet(() => setIsOpen(false))}
        </>
    );
};

const SuggestionsSkeleton = () => (
    <>
        {[0, 1, 2].map((row) => (
            <div key={row} className={SUGGESTION_ROW_CLASSES}>
                <div className="flex items-center gap-2">
                    <div className="h-3.5 w-12 shrink-0 animate-pulse rounded-[3px] bg-gray-200" />
                    <div className="h-3 flex-1 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="flex items-center gap-2.5">
                    <div className="h-2.5 w-1/3 flex-1 animate-pulse rounded bg-gray-200" />
                    <div className="h-6 w-20 shrink-0 animate-pulse rounded-[3px] bg-gray-200" />
                </div>
            </div>
        ))}
    </>
);
